<template>
  <div
    class="border border--radius-sm rounded q-ma-xs queue-picking-card"
    :class="[
      isOpen ? 'border--positive' : ''
    ]"
    @click="handleClick"
  >
    <div
      :class="isOpen ? 'border-bottom border-bottom--bold-1' : ''"
    >
      <!-- META: adapter + settings (left) | tasks (right rail, aligns with waves) -->
      <div class="row items-center q-px-sm q-pt-sm text-caption text-grey-7">
        <div class="col row items-center q-gutter-xs queue-card__meta-left">
          <q-badge
            dense
            :color="color"
            :label="$t(adapter)"
            class="text-capitalize"
          />

          <div
            v-if="data.settings && data.settings['picking-endpoint-place-type']"
            class="text-capitalize"
          >
            {{ data.settings['picking-endpoint-place-type'] }}
          </div>

          <div
            v-if="data.settings && data.settings['wave-order-limit'] && data.settings['wave-order-limit'] > 1"
            class="text-capitalize"
          >
            {{ data.settings['wave-order-limit'] }}
          </div>
        </div>
        <div class="queue-card__rail">
          <span v-if="tasks">{{ $t('Tasks') + ': ' + tasks }}</span>
        </div>
      </div>

      <!-- MAIN: name (left) + wave/batch count (right rail) -->
      <div class="row items-center no-wrap q-px-sm q-pb-sm q-pt-xs">
        <div
          class="col text-subtitle1 text-weight-medium text-capitalize queue-card__name"
        >
          {{ data.name }}
        </div>
        <div
          v-if="showActiveBatchCount"
          class="text-subtitle2 text-weight-medium queue-card__rail queue-card__waves"
          :class="sequenceCountColorClass"
        >
          {{ data._activeSequences }}
          {{ activeBatchSuffix }}
        </div>
      </div>

      <div
        v-if="sequences.length > 0"
        class="text-caption text-grey-7 q-px-sm q-pb-xs"
      >
        {{ isOpen ? $t('Collapse') : $t('Expand') }}
      </div>
    </div>

    <q-slide-transition>
      <div
        v-if="sequences && sequences.length > 0 && isOpen"
        class="q-px-sm q-pb-sm"
      >
        <terminal-sequence-card
          v-for="(sequence, i) in sequences"
          :key="sequence.id"
          :sequence="sequence"
          show-ext-id
          :last-in-list="i === sequences.length - 1"
          @click="handleSequence($event)"
        />
      </div>
    </q-slide-transition>
  </div>
</template>

<script>
import TerminalSequenceCard from './terminal-sequence-card.vue'
import {
  activeQueueCountSuffix,
  shouldShowActiveQueueCount
} from '../../utils/picking-queue-active-count'

export default {
  name: 'PickingQueue',
  components: {
    TerminalSequenceCard
  },
  emits: ['click'],
  props: {
    data: {
      type: Object,
      default () {
        return {
          options: []
        }
      }
    },
    rawData: {
      type: Object,
      default () {
        return {}
      }
    }
  },
  data () {
    return {
      isOpen: false,
      didAutoExpandSequences: false,
      adapterColors: {
        routed_discrete: 'amber',
        routed_batch: 'green',
        totes: 'blue'
      },
      adapterLabels: {
        routed_discrete: 'By ID',
        routed_batch: 'Wave'
      }
    }
  },
  watch: {
    sequences: {
      immediate: true,
      handler (seq) {
        if (
          !this.didAutoExpandSequences &&
          Array.isArray(seq) &&
          seq.length > 0
        ) {
          this.isOpen = true
          this.didAutoExpandSequences = true
        }
      }
    }
  },
  computed: {
    color () {
      if (this.data.adapter === 'routed_batch' && this.data.settings && this.data.settings['wave-order-limit'] === 1) {
        return 'pink'
      }

      return this.adapterColors[this.data.adapter]
    },
    adapter () {
      if (this.data.adapter === 'routed_batch' && this.data.settings && this.data.settings['wave-order-limit'] === 1) {
        return 'Order by order'
      }

      return this.adapterLabels[this.data.adapter] || this.data.adapter
    },
    sequences () {
      return (this.data.sequences || []).filter(x => {
        // if (x && x.state === 'processing') {
        //   return x
        // }

        // return x && x.count && x.count > 0
        return x
      })
    },
    tasks () {
      if (this.data.tasks) {
        if (typeof this.data.tasks === 'object') {
          return ''
        }

        return this.data.tasks
      }

      return (this.data.options || [])
        .filter(val => val)
        .reduce((acc, val) => acc + Number(val.count), 0)
    },
    showActiveBatchCount () {
      return this.data.adapter === 'routed_batch' &&
        shouldShowActiveQueueCount(this.data._activeSequences)
    },
    activeBatchSuffix () {
      const n = Number(this.data._activeSequences)
      return activeQueueCountSuffix(n, this.$i18n.locale, k => this.$t(k))
    },
    /** 0 green, positive count yellow, loading grey */
    sequenceCountColorClass () {
      const n = this.data._activeSequences
      if (n === undefined || n === null) {
        return 'text-grey-7'
      }
      const num = Number(n)
      if (Number.isNaN(num)) {
        return 'text-grey-7'
      }
      if (num === 0) {
        return 'text-positive'
      }
      return 'text-warning'
    }
  },
  methods: {
    handleSequence (sequence) {
      this.$emit('click', { ...this.rawData, sequence })
    },
    handleClick () {
      if (this.sequences && this.sequences.length > 0) {
        this.isOpen = !this.isOpen
        return
      }

      const firstSeq = (this.data.sequences && this.data.sequences[0]) || undefined

      const data = {
        ...this.rawData,
        sequence: firstSeq
      }

      this.$emit('click', data)
    }
  }
}
</script>

<style scoped>
.queue-card__meta-left,
.queue-card__name {
  min-width: 0;
  word-break: break-word;
}

.queue-card__rail {
  flex-shrink: 0;
  min-width: 80px;
  text-align: right;
}
</style>
